import { getLongMonthDateWithoutYear } from '@lmt/unicorn-shared/app/components/patients/common';
import { EmptyBlock } from '@lmt/unicorn-shared/app/components/patients/profile/overview/components/EmptyBlock';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { addDays } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { NightViewInfo, NightViewItem } from './NightViewItem';

import styles from './NightViewPanel.module.scss';
import { useOnlineMeasurementSettings } from '../../patients/list/hooks/useOnlineMeasurementSettings';

function isActualVisit(startDate: Date, endDate: Date, actualDate: Date) {
  return (
    startDate.getDate() === actualDate.getDate()
    //|| endDate.getDate() === actualDate.getDate()
    //|| (startDate < actualDate && endDate > actualDate)
  );
}

export interface NightViewPanelProps {
  nightVisits: NightViewInfo[];
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
  openMeasurementTab: (
    patientId: number,
    caseId: number,
    visitId: number,
    measurementId: number,
  ) => void;
  startMiniscreenOnline: boolean;
  startMiniscreenOffline: boolean;
}

export const NightViewPanel: React.FC<NightViewPanelProps> = (props) => {
  const { nightVisits, openPatientTab } = props;
  const i18next = useScopedTranslation('dashboard.night_view');
  const [currentTab, setCurrentTab] = useState<
    'yesterday' | 'today' | 'tomorrow'
  >('today');
  const today = new Date();
  const tommorrow = addDays(today, 1);
  const yesterday = addDays(today, -1);

  const filteredVisits = nightVisits.filter((visit) =>
    currentTab === 'today'
      ? isActualVisit(visit.startDate, visit.endDate, today)
      : currentTab === 'yesterday'
        ? isActualVisit(visit.startDate, visit.endDate, yesterday)
        : isActualVisit(visit.startDate, visit.endDate, tommorrow),
  );

  const hasPlaceColumn = useMemo(
    () => filteredVisits.some((visit) => visit.room),
    [filteredVisits],
  );

  const { currentRoom, isOnlineMeasurementsDisabled } =
    useOnlineMeasurementSettings();

  return (
    <div className={styles.container} data-test-id="night-view-block">
      <div className={styles.header}>
        <div className={styles.title}>{i18next.t('title')}</div>
        <div className={styles.tabs} data-test-id="night-view-block-tabs">
          <div
            className={styles.tab}
            data-selected={currentTab === 'yesterday'}
            onClick={() => setCurrentTab('yesterday')}
          >
            {`${i18next.t('yesterday')}, ${getLongMonthDateWithoutYear(yesterday)}`}
          </div>
          <div
            className={styles.tab}
            data-selected={currentTab === 'today'}
            onClick={() => setCurrentTab('today')}
          >
            {`${i18next.t('today')}, ${getLongMonthDateWithoutYear(today)}`}
          </div>
          <div
            className={styles.tab}
            data-selected={currentTab === 'tomorrow'}
            onClick={() => setCurrentTab('tomorrow')}
          >
            {`${i18next.t('tomorrow')}, ${getLongMonthDateWithoutYear(
              tommorrow,
            )}`}
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {filteredVisits.length === 0 ? (
          <EmptyBlock
            text={i18next.t('empty')}
            data-test-id="night-view-block-content"
          />
        ) : (
          <table className={styles.visitsTable}>
            <tbody>
              {filteredVisits.map((visit) => {
                return (
                  <NightViewItem
                    startMiniscreenOffline={props.startMiniscreenOffline}
                    startMiniscreenOnline={props.startMiniscreenOnline}
                    isStartDateHidden={
                      currentTab === 'today'
                        ? visit.startDate.getDate() !== today.getDate()
                        : currentTab === 'yesterday'
                          ? visit.startDate.getDate() !== yesterday.getDate()
                          : visit.startDate.getDate() !== tommorrow.getDate()
                    }
                    visit={visit}
                    hasPlaceColumn={hasPlaceColumn}
                    openPatientTab={openPatientTab}
                    openCaseVisitTab={props.openCaseVisitTab}
                    openMeasurementTab={props.openMeasurementTab}
                    currentRoom={currentRoom}
                    isOnlineMeasurementsDisabled={isOnlineMeasurementsDisabled}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
